import { useCallback, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { useTracking } from "utilities/analytics/analytics";

import type { ISearchHit } from "commonComponents/utilities/MainNavigation/SearchDialog/search/search.types";

/**
 * `Learn` telemetry for the search dialog, sent through `useTracking` (Sawmill).
 *
 * Logs the join key (`search_id`, which the service records as
 * `btsearch.client_search_id`) plus what only the browser sees: clicks, ranks,
 * abandonment, unroutable drops, felt latency. Ids, counts, durations and enums
 * only — no titles, summaries or prose. The exceptions are deliberate: `query`,
 * and the typing assists' `typed`, `offered` and `typedAtEnd` (see
 * {@link IAssistShown}, {@link IAssistOutcome}).
 */

/**
 * Downstream pipelines filter on `component`, so this keeps the dialog a
 * separate series from the retired dropdown's.
 */
const COMPONENT = "SearchDialog";

/** Caps the dropped-shape breakdown so one bad payload can't inflate an event. */
const MAX_DROPPED_SHAPES = 10;

/** Caps an assist's text fields. */
export const MAX_ASSIST_TEXT = 200;
/** Caps an assist list's entity breakdown. */
const MAX_ASSIST_ROWS = 12;

export type SearchDialogSurface = "dialog" | "askPage";

/** How the dialog was opened. */
export type DialogTrigger = "icon" | "hotkey";

/**
 * Why the dialog closed, in the user's terms rather than the code's.
 *
 * `abandoned` is the one that matters: the service sees a search with no
 * successor and cannot tell "read the answer off the list and moved on" from
 * "gave up".
 */
export type DialogOutcome = "abandoned" | "record_opened" | "handed_off";

/**
 * Which list a rank indexes. The dialog only ever sends `committed` — it
 * renders nothing else — but the stream carries both, so the field stays
 * rather than the service having to assume.
 */
export type RankSource = "committed" | "preview";

/** What armed Ask — the service computes its own `is_question` and cannot know this. */
export type AskArmedBy = "auto" | "toggle" | "zero-results";

export type NavMethod = "click" | "keyboard";

export type RecordOpenedFrom = "result" | "recent" | "citation";

/**
 * How far one search got. Each is sent at most once per search.
 *
 * - `complete`: the stream finished.
 * - `interrupted`: the dialog closed, or the input was cleared, while the
 *   search was still running, with no row from it opened and nothing handed
 *   to Ask.
 * - `abandoned`: the search completed, then the dialog closed or the input was
 *   cleared with no row from it opened and nothing handed to Ask.
 *
 * A search superseded by the next one sends nothing further.
 */
export type SearchPhase = "complete" | "interrupted" | "abandoned";

/** A record the client could not route, named by shape rather than identity. */
export interface IDroppedShape {
    entity: string;
    /** The payload keys the hit DID carry, so the missing one is inferable. */
    payloadKeys: string[];
}

export interface IRoutability {
    renderedCount: number;
    droppedUnroutable: number;
    droppedShapes: IDroppedShape[];
}

/**
 * Routable/dropped split of a committed ranking, one aggregate per search.
 * `payloadKeys` names what a dropped hit did carry, without logging the record.
 */
export function summarizeRoutability(
    committed: readonly ISearchHit[] | null,
    isRoutable: (hit: ISearchHit) => boolean,
): IRoutability {
    const hits = committed ?? [];
    const dropped = hits.filter((hit) => !isRoutable(hit));
    const shapes = new Map<string, IDroppedShape>();
    for (const hit of dropped) {
        const keys = Object.keys(hit.payload ?? {}).sort();
        const key = `${hit.entity}:${keys.join(",")}`;
        if (!shapes.has(key)) {
            shapes.set(key, { entity: hit.entity, payloadKeys: keys });
        }
    }
    return {
        renderedCount: hits.length - dropped.length,
        droppedUnroutable: dropped.length,
        droppedShapes: [...shapes.values()],
    };
}

export interface ISearchDialogLearnContext {
    surface: SearchDialogSurface;
}

interface IEnvelopeIds {
    searchId: string | null;
    traceId: string | null;
    sessionId?: string | null;
    turnId?: string | null;
}

export interface ISearchDialogLearn {
    /** The id joining every event from one opening of the dialog. */
    openId: string;
    learnDialogOpened: (payload: {
        trigger: DialogTrigger;
        openRecordEntity: string | null;
        recentCount: number;
        promptCount: number;
    }) => void;
    learnDialogClosed: (payload: {
        outcome: DialogOutcome;
        msOpen: number;
        searchCount: number;
        askCount: number;
        sawResults: boolean;
    }) => void;
    learnSearchPhase: (
        ids: IEnvelopeIds,
        payload: {
            searchPhase: SearchPhase;
            query: string;
            resultCount: number;
            renderedCount: number;
            droppedUnroutable: number;
            droppedShapes: IDroppedShape[];
            rankSource: RankSource;
            durationMs: number | undefined;
            /** Until the service's ranking landed and its rows rendered, if it did. */
            timeToRowsMs: number | undefined;
        } & Partial<IAssistLed>,
    ) => void;
    learnRecordOpened: (
        ids: IEnvelopeIds,
        payload: {
            from: RecordOpenedFrom;
            entity: string;
            recordId: string;
            rank: number | null;
            rankSource: RankSource | null;
            navMethod: NavMethod;
            timeToClickMs: number | undefined;
            msFromOpen: number;
        },
    ) => void;
    learnAskArmed: (ids: IEnvelopeIds, payload: { armedBy: AskArmedBy }) => void;
    learnPromptPicked: (payload: { promptIndex: number; promptCount: number }) => void;
    learnCorrectionApplied: (ids: IEnvelopeIds) => void;
    /**
     * The inline guess taken (Tab, right arrow or End). Lengths and where the
     * guess came from, never the text.
     */
    learnGuessAccepted: (
        ids: IEnvelopeIds,
        payload: {
            key: GuessKey;
            guessFrom: GuessSource;
            typedLength: number;
            guessLength: number;
        },
    ) => void;
    /** A typeahead name picked to search for: its 1-based place and kind, never the name. */
    learnSuggestionPicked: (
        ids: IEnvelopeIds,
        payload: { rank: number; count: number; entity: string; navMethod: NavMethod },
    ) => void;
    /** A suggested question picked (Needs attention): its place and tone, never the text. */
    learnSuggestedQuestionPicked: (payload: {
        rank: number;
        count: number;
        source: string;
        tone: string;
        hadCount: boolean;
    }) => void;
    /**
     * A typing assist came on screen. Returns its `assistId`, which its end
     * event and any search it led to carry.
     */
    learnAssistShown: (ids: IEnvelopeIds, payload: IAssistShown) => string;
    /** How a shown assist ended: exactly once per `learnAssistShown`. */
    learnAssistEnded: (ids: IEnvelopeIds, assistId: string, payload: IAssistOutcome) => void;
}

/** Which key took the inline guess. */
export type GuessKey = "tab" | "arrow" | "end";
/** Where the inline guess came from: the server's whole-name completion, a predicted query, or a listed name. */
export type GuessSource = "completion" | "prediction" | "name";

/**
 * The three typing assists, each logged through one lifecycle (see
 * {@link IAssistOutcome}):
 *
 * - `did_you_mean`: the service's correction of the text typed.
 * - `inline_completion`: the gray guess after the caret.
 * - `query_suggestions`: the list of names (dialog) or questions (Ask page)
 *   under the box.
 */
export type AssistKind = "did_you_mean" | "inline_completion" | "query_suggestions";

/**
 * What became of one offer: shown once, then exactly one of these.
 *
 * - `accepted`: taken (applied, Tab/arrow/End, picked).
 * - `typed_through`: the user typed the offered text themselves (for a list,
 *   one of its rows), so the offer was right but not used.
 * - `refined`: the user kept typing in its direction and a newer offer took
 *   its place (for a list, the usual end).
 * - `overridden`: the user typed on to text the offer does not lead to.
 * - `backed_off`: the user deleted back past where it was offered.
 * - `dismissed`: Escape, or the caret or a click moved off it.
 * - `expired`: it went away without the user acting on it (a search ran, a
 *   newer answer, the question was sent, the surface closed).
 */
export type AssistOutcome =
    | "accepted"
    | "typed_through"
    | "refined"
    | "overridden"
    | "backed_off"
    | "dismissed"
    | "expired";

/**
 * An offer as it was shown: what was typed and what was offered, both as text.
 * Judging an assist needs the offer itself (was the correction right, which
 * row was taken over which), so unlike the rest of this module these carry a
 * record's name or a question built from one. Capped (MAX_ASSIST_TEXT chars,
 * MAX_ASSIST_ROWS rows) so a long list cannot inflate an event.
 */
export interface IAssistShown {
    kind: AssistKind;
    /**
     * What produced it: for the guess a {@link GuessSource} or the Ask page's
     * completion kind; for a correction the service's layer when it names one
     * ("service" otherwise); for a list "names", "attention" or "ask".
     */
    source: string;
    typed: string;
    /**
     * The text taking it would put in the box: the correction, the box's text
     * with the guess taken, or each list row in order.
     */
    offered: readonly string[];
    /** Rows in a list; 1 for a correction or a guess. */
    count: number;
    /** For a correction or a guess: the offered text's length. */
    offeredLength?: number;
    /** For a correction or a guess: edits from what was typed to it (capped). */
    editDistance?: number;
    /** For a correction or a guess: it only adds to what was typed. */
    extendsTyped?: boolean;
    /** For a list: each row's entity kind (dialog) or row kind (Ask), in order. */
    entities?: string[];
    /** For a list: each row's match layer from the service, in order, where it sent one. */
    layers?: string[];
    /** For a list: rows whose text starts with what was typed (the rest are sound-alikes). */
    prefixRows?: number;
}

/** How one offer ended. */
export interface IAssistOutcome {
    kind: AssistKind;
    outcome: AssistOutcome;
    /** How long it was on screen. */
    msShown: number;
    /** The text in the box when it ended, as text: an Ask question in full (capped). */
    typedAtEnd: string;
    /** For a list: the 1-based row taken. */
    rank?: number;
    navMethod?: NavMethod | GuessKey;
    /** For a list: keyboard steps through its rows before it ended. */
    highlights?: number;
}

/** The assist a search came from, stamped on that search's phase events. */
export interface IAssistLed {
    ledByAssist: AssistKind;
    ledByAssistId: string;
}

/**
 * Builds the dialog's telemetry callbacks, all sharing one `openId`.
 *
 * A hook rather than bare functions because `useTracking` is one, and because
 * the `openId` has to be minted once per mount — the dialog is mounted on open
 * and unmounted on close, so its lifetime is exactly the episode the id names.
 */
export function useSearchDialogLearn(context: ISearchDialogLearnContext): ISearchDialogLearn {
    const { trackEvent } = useTracking({ component: COMPONENT });
    // Lazy state rather than a ref: a ref would have to be read during render
    // to seed it, and the value only has to survive re-renders, not be mutable.
    const [openId] = useState(() => uuidv4());
    const { surface } = context;

    const envelope = useCallback(
        (ids?: IEnvelopeIds) => ({
            openId,
            surface,
            searchId: ids?.searchId ?? null,
            traceId: ids?.traceId ?? null,
            sessionId: ids?.sessionId ?? null,
            turnId: ids?.turnId ?? null,
        }),
        [openId, surface],
    );

    return useMemo<ISearchDialogLearn>(
        () => ({
            openId,

            learnDialogOpened: (payload) =>
                trackEvent({
                    event: "ModalOpen",
                    extraInfo: { ...envelope(), action: "dialog_opened", ...payload },
                }),

            learnDialogClosed: (payload) =>
                trackEvent({
                    event: "ModalClose",
                    extraInfo: { ...envelope(), action: "dialog_closed", ...payload },
                }),

            learnSearchPhase: (ids, payload) =>
                trackEvent({
                    event: "Search",
                    extraInfo: {
                        ...envelope(ids),
                        ...payload,
                        droppedShapes: payload.droppedShapes.slice(0, MAX_DROPPED_SHAPES),
                    },
                }),

            learnRecordOpened: (ids, payload) =>
                trackEvent({
                    event: "LinkClick",
                    element: "Link",
                    uniqueId: payload.entity,
                    extraInfo: { ...envelope(ids), action: "record_opened", ...payload },
                }),

            learnAskArmed: (ids, payload) =>
                trackEvent({
                    event: "OptionSelected",
                    extraInfo: { ...envelope(ids), action: "ask_armed", ...payload },
                }),

            learnPromptPicked: (payload) =>
                trackEvent({
                    event: "OptionSelected",
                    // The index, never the prompt text: the list is fixed and
                    // curated, so the index identifies it exactly.
                    extraInfo: { ...envelope(), action: "prompt_picked", ...payload },
                }),

            learnCorrectionApplied: (ids) =>
                trackEvent({
                    event: "OptionSelected",
                    extraInfo: { ...envelope(ids), action: "correction_applied" },
                }),

            learnGuessAccepted: (ids, payload) =>
                trackEvent({
                    event: "OptionSelected",
                    extraInfo: { ...envelope(ids), action: "guess_accepted", ...payload },
                }),

            learnSuggestionPicked: (ids, payload) =>
                trackEvent({
                    event: "OptionSelected",
                    extraInfo: { ...envelope(ids), action: "suggestion_picked", ...payload },
                }),

            learnSuggestedQuestionPicked: (payload) =>
                trackEvent({
                    event: "OptionSelected",
                    extraInfo: { ...envelope(), action: "suggested_question_picked", ...payload },
                }),

            learnAssistShown: (ids, payload) => {
                const assistId = uuidv4();
                trackEvent({
                    event: "PopoverOpen",
                    extraInfo: {
                        ...envelope(ids),
                        action: "assist_shown",
                        assistId,
                        ...payload,
                        typed: payload.typed.slice(0, MAX_ASSIST_TEXT),
                        offered: payload.offered
                            .slice(0, MAX_ASSIST_ROWS)
                            .map((text) => text.slice(0, MAX_ASSIST_TEXT)),
                        entities: payload.entities?.slice(0, MAX_ASSIST_ROWS),
                        layers: payload.layers?.slice(0, MAX_ASSIST_ROWS),
                    },
                });
                return assistId;
            },

            learnAssistEnded: (ids, assistId, payload) =>
                trackEvent({
                    event: "PopoverClose",
                    extraInfo: {
                        ...envelope(ids),
                        action: "assist_ended",
                        assistId,
                        ...payload,
                        typedAtEnd: payload.typedAtEnd.slice(0, MAX_ASSIST_TEXT),
                    },
                }),
        }),
        [openId, envelope, trackEvent],
    );
}
